import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import Icon from '../../Common/Icon';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../../Common/InputText';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import MentionableTextArea from '../../Common/MentionableTextArea/MentionableTextArea';
import Button from '../../Common/Button';
import Tag from '../../Common/Tag/Tag';

import { MAX_TASK_STEP_COUNT } from '../../../constants/constants';
import Routes from '../../../constants/routes';

import CommentRow from './CommentRow';
import HistoryRow from './HistoryRow';
import FilesAndLinksSection from './FilesAndLinksSection';
import Toggle from '../../Common/Toggle/Toggle';

import Rater from 'react-rater';

/**
 * Edit a limited portion of any type of task, whether created by pushing down
 * a corporate-created baseline task to a location or a task created by a user
 * directly at a single location.  Limited Mode only allows editing of certain
 * fields for two reason:
 *
 * 1) A Baseline-Created Location Task simply is not allowed to change any
 *    fields other than the ones allowed via this drawer.
 * 2) We save time/complexity by re-using these editing abilities on this
 *    drawer instead of trying to also include them in the LocationTask drawer.
 */
const LimitedModeTaskDetailDrawerForm = ({
	task,
	isShowingBasicInfo,

	isFormValid,
	isCommentsLoading,
	isLoadingProjectCoaches,

	currentUserId,
	customerRole,

	onTaskChange,
	onShowHistory,
	saveCommentClick,
	onShowMoreComments,
	onDragEnd,
	toggleStepCompleted,
	onUpdateStepName,
	onAddStep,
	toggleStepEdit,
	toggleCertified,
	toggleEditProjectCoach,
	onToggleBasicInfo,
	toggleFeedBackModal,

	showContingentDrawer,
	showPrereqDrawer,

	allowLocationTaskBudgetEntry,
	allowLocationTaskDateChanges,
	isProjectWidgetActive,
	isTaskFeedbackActive,
	isTaskFinancialsActive,
	isTaskStepsActive,
	projectCoaches,
	mentionableUsers,
	responsibleUsers,
	statuses,
	taskTags,

	isSavingLink,
	isSavingFile,
	locationTaskActions,
	filesActions,
	setTaskLinks,
	onSaveLink,
	setSavingLink,
	uploadFile,
	setTaskFiles,
	onSaveFile,
	setSavingFile,

	isMobile,

	locationTaskTranslation,
	locationTaskTranslationRating,
	onLocationTaskTranslation,
	onLocationTaskTranslationRating,
	canTranslate,
	translationLabels,
	isTranslatingLocationTask,
}) => {
	const getItemStyle = (isDragging, draggableStyle) => ({
		userSelect: 'none',
		lineHeight: 1,
		margin: `0 0 -26px 0`,
		background: 'white',
		border: isDragging ? '2px solid #03a9f4' : '',
		// styles we need to apply on draggables
		...draggableStyle,
	});

	const getListStyle = (isDraggingOver) => ({
		background: isDraggingOver ? '#eee' : 'white',
		borderRadius: '0.25rem',
		padding: 8,
	});

	/**
	 * Private function that properly formats the new task
	 * before passing it up the component chain to the
	 * TaskDetailManager
	 */
	const onTaskFieldChange = (name, value) => {
		const updatedTask = { ...task };
		updatedTask[name] = value;
		onTaskChange(updatedTask);
	};

	const onDropdownChange = (value, name) => {
		// Do not send a null value on a dropdown change
		if (value) {
			onTaskFieldChange(name, value);
		}
	};

	const onMultiSelectChange = (value, name) => {
		value = value.split(',');
		onTaskFieldChange(name, value);
	};

	const onTextFieldChange = (e) => {
		if (!e || !e.target || !e.target.name) {
			return;
		}

		onTaskFieldChange(e.target.name, e.target.value);
	};

	const onBudgetFieldChange = (e) => {
		e.target.value = parseInt(e.target.value);
		onTaskFieldChange(e.target.name, e.target.value);
	};

	const onToggleChange = (name, checked) => {
		onTaskFieldChange(name, checked);
	};

	const onAssignToMe = () => {
		onTaskFieldChange('responsibleUserId', currentUserId);
	};

	const onUnassign = () => {
		onTaskFieldChange('responsibleUserId', 0);
	};

	const onFinishEditingStep = (step, e) => {
		if (e.keyCode == 13) {
			e.preventDefault();
			e.stopPropagation();
			step.isEditing = false;
			onUpdateStepName(step, e);
		}
	};

	const isAssignedToMe = task && task.responsibleUserId && currentUserId.toString() === task.responsibleUserId.toString();
	const showContactSection = task.contactEmail && task.contactEmail.trim().length > 0 && task.contactName && task.contactName.trim().length > 0;

	return (
		<div className='task-detail-wrapper '>
			{task.successorTasks.length > 0 && (
				<div className='row'>
					<div className='col-12'>
						<div className='alert alert-secondary prereq-alert'>
							<span className='font-weight-bold'>Prerequisites for this task:</span>
							{task.successorTasks.map((task) => (
								<div key={task.id}>
									<button className='btn btn-link' onClick={(e) => showPrereqDrawer(e, task.id)}>
										<span className={`task-status-label core-status-${task.coreStatusId}`}>{task.status.toUpperCase()}</span>
										{task.name}
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{task.contingentTaskName !== '' && task.contingentTaskName && (
				<div className='row'>
					<div className='col-12'>
						<div className='alert alert-secondary contingent-alert'>
							<div className='font-weight-bold me-2'>This task is a prerequisite of:</div>
							<div>
								<button className='btn btn-link' onClick={(e) => showContingentDrawer(e)}>
									<span className={`task-status-label core-status-${task.contingentTaskCoreStatusId}`}>
										{task.contingentTaskStatus.toUpperCase()}
									</span>
									{task.contingentTaskName}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<form noValidate className={isFormValid ? '' : 'was-validated'}>
				<div className='row'>
					<div className='col-sm-6 col-xs-12'>
						<div className='task-detail-section'>
							<div className='title highlighted'>{task.isMilestone ? 'Milestone Task' : 'Task'}</div>
							<div className='sub-title multiline'>{task.name}</div>
							{task.additionalInfo !== '' ? <div className='content multiline'>{task.additionalInfo}</div> : null}
							<div className='basic-info-section'>
								{task.anticipatedEffort !== '' && (
									<Fragment>
										<span className='info-key'>Anticipated Effort:</span>
										<span className='info-value'>{task.anticipatedEffort}</span>
									</Fragment>
								)}
							</div>
							{isTaskFeedbackActive === true && task.baselineTaskId && task.baselineTaskId > 0 ? (
								<div className='basic-info-section'>
									<button className='btn btn-link float-end' onClick={toggleFeedBackModal}>
										Feedback
									</button>
								</div>
							) : null}

							{!locationTaskTranslation && canTranslate && (
								<div className='basic-info-section'>
									<LoadingIndicator isLoading={isTranslatingLocationTask}>
										<Button
											type='button'
											bootstrapClass='link float-start'
											onClick={onLocationTaskTranslation}
											text={translationLabels.translateLabel}
										/>
									</LoadingIndicator>
								</div>
							)}
						</div>

						{locationTaskTranslation && (
							<>
								<div className='task-detail-section'>
									<div className='sub-title multiline'>{locationTaskTranslation.name}</div>

									<div className='content multiline'>{locationTaskTranslation.additionalInfo}</div>
								</div>

								<div className='task-detail-section'>
									<span>{translationLabels.translationRatingLabel}</span>

									<br />

									<Rater
										total={5}
										rating={locationTaskTranslationRating}
										interactive={true}
										onRate={(e) => onLocationTaskTranslationRating(locationTaskTranslation.id, e.rating)}
									/>
								</div>
							</>
						)}

						<div className='task-detail-section mt-3'>
							<div className='title highlighted'>Resources</div>
							{task.projectName && isProjectWidgetActive === true && (
								<Fragment>
									{task.isEditingProjectCoach === true ? (
										<div style={{ position: 'relative' }}>
											<LoadingIndicator isLoading={isLoadingProjectCoaches}>
												<div className='task-detail-section'>
													<MultiSelectBox
														name='projectCoachId'
														label='Project Coach'
														idField='id'
														valueField='name'
														options={projectCoaches}
														selectedValues={task.projectCoachId.toString()}
														onChange={onDropdownChange}
														isRequired={task.projectName && isProjectWidgetActive === true}
														isMulti={false}
													/>
												</div>
											</LoadingIndicator>
										</div>
									) : (
										<div className='basic-info-section'>
											<span className='info-key'>Project Coach:</span>
											{task.projectCoach ? (
												<span className='info-value'>
													{task.projectCoach}
													<button className='btn btn-link float-end' onClick={toggleEditProjectCoach}>
														Change
													</button>
												</span>
											) : (
												<span className='info-value'>
													Not Assigned
													<button className='btn btn-link float-end' onClick={toggleEditProjectCoach}>
														Assign
													</button>
												</span>
											)}
										</div>
									)}
								</Fragment>
							)}
							<FilesAndLinksSection
								task={task}
								isSavingLink={isSavingLink}
								isSavingFile={isSavingFile}
								locationTaskActions={locationTaskActions}
								filesActions={filesActions}
								setTaskLinks={setTaskLinks}
								onSaveLink={onSaveLink}
								setSavingLink={setSavingLink}
								uploadFile={uploadFile}
								setTaskFiles={setTaskFiles}
								onSaveFile={onSaveFile}
								setSavingFile={setSavingFile}
								isMobile={isMobile}
							/>
						</div>

						{showContactSection && (
							<div className='task-detail-section'>
								<div className='title'>Contact for Assistance</div>
								<div className='content'>
									{task.contactName} | <a href={'mailto:' + task.contactEmail}>{task.contactEmail}</a>
								</div>
							</div>
						)}

						{isMobile === false && (
							<div className='task-detail-section'>
								<div className='title'>Basic Information</div>
								{isShowingBasicInfo ? (
									<Fragment>
										{task.projectName !== '' ? (
											<div className='basic-info-section'>
												<span className='info-key'>Project Name:</span>
												<span className='info-value'>{task.projectName}</span>
											</div>
										) : null}

										{task.departmentName !== '' ? (
											<div className='basic-info-section'>
												<span className='info-key'>Department Name:</span>
												<span className='info-value'>{task.departmentName}</span>
											</div>
										) : null}

										{task.communityName !== '' ? (
											<div className='basic-info-section'>
												<span className='info-key'>Community Name:</span>
												<span className='info-value'>{task.communityName}</span>
											</div>
										) : null}

										{task.phaseName !== '' ? (
											<div className='basic-info-section'>
												<span className='info-key'>Phase Name:</span>
												<span className='info-value'>{task.phaseName}</span>
											</div>
										) : null}

										<div className='basic-info-section'>
											<span className='info-key'>Location Task Id:</span>
											<span className='info-value'>{task.id}</span>
										</div>

										{task.baselineId !== 0 &&
											task.baselineTaskId !== 0 &&
											isMobile === false &&
											(customerRole === 'Subscription Moderator' || customerRole === 'Admin' || customerRole === 'Owner') ? (
											<div className='basic-info-section'>
												<span className='info-key'>Baseline Task Id:</span>
												<a
													href={Routes.BaselineTaskList + '/?bid=' + task.baselineId + '&btid=' + task.baselineTaskId}
													target='_blank'
													rel='noopener noreferrer'>
													<span>{task.baselineTaskId}</span>
												</a>
											</div>
										) : null}
										{isProjectWidgetActive === false && (
											<Toggle
												name='isLocationTaskDateAdjustable'
												text='Adjust Dates if Opening Date changes'
												checked={task.isLocationTaskDateAdjustable}
												onChange={onToggleChange}
												isDisabled={!allowLocationTaskDateChanges}
											/>
										)}

										<Button type='button' bootstrapClass='link' onClick={onToggleBasicInfo} text='Hide Info' />
									</Fragment>
								) : (
									<Button type='button' bootstrapClass='link' onClick={onToggleBasicInfo} text='Show Info' />
								)}
							</div>
						)}
					</div>

					<div className='col-sm-6 col-xs-12'>
						{task.includeCertificationCheckbox === true ? (
							<Fragment>
								<div className='task-detail-section'>
									<span className='title me-2'>Certify:</span>
									<span className='cert-box' onClick={toggleCertified}>
										{task.isProjectCertified === true ? (
											<Icon name='check-square text-success' />
										) : (
											<Icon stylePrefix='far' name='square' />
										)}
									</span>
									<br />
									<br />
									<div className='alert alert-secondary'>
										Certifying completion means that all applicable tasks to date and associated with this project are complete.
									</div>
								</div>
							</Fragment>
						) : null}

						<div className='task-detail-section'>
							<div className='title highlighted'>Status</div>
							<MultiSelectBox
								name='statusId'
								idField='id'
								valueField='name'
								options={statuses}
								selectedValues={task.statusId.toString()}
								onChange={onDropdownChange}
								isRequired={true}
								errorMessage='Status is required'
								isMulti={false}
							/>
						</div>

						<div className='task-detail-section responsible-user-section'>
							<div className='title'>Responsible User</div>
							<MultiSelectBox
								className='responsible-user'
								name='responsibleUserId'
								idField='id'
								valueField='name'
								options={responsibleUsers}
								selectedValues={task.responsibleUserId.toString()}
								onChange={onDropdownChange}
								isRequired={false}
								errorMessage='Responsible User is required'
								isMulti={false}
							/>
							{!isAssignedToMe && (
								<button className='me-3 btn btn-link assign-to-me' onClick={onAssignToMe}>
									Assign Task to Me
								</button>
							)}
							{task.responsibleUserId > 0 && (
								<button className='btn btn-link unassign' onClick={onUnassign}>
									Unassign
								</button>
							)}
							<div className='basic-info-section'>
								<span className='info-key'>Suggested Responsible:</span>
								<span className='info-value'>{task.jobTitleName}</span>
							</div>
						</div>

						{task.baselineTaskTags && task.baselineTaskTags.length > 0 && (
							<div className='task-detail-section baseline-task-tags-section'>
								<div className='display-tags'>
									{task.baselineTaskTags.map((tag) => (
										<Tag key={tag.taskTagId} backgroundColor={tag.backgroundColor} color={tag.fontColor} title={tag.title} />
									))}
								</div>
							</div>
						)}
						<div className='task-detail-section location-task-tags-section'>
							<MultiSelectBox
								name='locationTaskTagIds'
								options={taskTags}
								idField='id'
								valueField='name'
								selectedValues={task.locationTaskTagIds.toString()}
								onChange={onMultiSelectChange}
								isMulti={true}
								label='Tags'
							/>
						</div>

						<LoadingIndicator isLoading={isCommentsLoading}>
							<div className='task-detail-section'>
								<div className='title highlighted'>Task Comments</div>
								<div className='add-comment-section'>
									<MentionableTextArea
										name='comment'
										value={task.comment.toString()}
										onChange={onTextFieldChange}
										data={mentionableUsers}
										maxLength={2000}
									/>
									<div>
										<button type='button' className='btn btn-secondary' onClick={saveCommentClick}>
											Post
										</button>
									</div>
								</div>
								<div className='list-group list-group-flush'>
									{task.comments && task.comments.length > 0 ? (
										task.comments.map((c) => (
											<CommentRow
												key={c.id}
												comment={c}
												locationTaskActions={locationTaskActions}
												mentionableUsers={mentionableUsers}
											/>
										))
									) : (
										<div className='content'>No comments</div>
									)}
								</div>
								{task.comments && task.commentTotal > task.comments.length ? (
									<div>
										<Button type='button' bootstrapClass='link' onClick={onShowMoreComments} text='Show More' />
									</div>
								) : null}
							</div>
						</LoadingIndicator>

						{isTaskStepsActive === true ? (
							<div className='task-detail-section'>
								<div className='title highlighted'>Task Steps</div>
								<DragDropContext onDragEnd={onDragEnd}>
									<Droppable droppableId='droppable'>
										{(provided, snapshot) => (
											<div className='step-container' ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
												{task.steps.map((step, index) => (
													<Draggable
														key={step.ordinal}
														draggableId={step.ordinal.toString()}
														index={index}
														isDragDisabled={step.baselineTaskStepId > 0 || step.isEditing === false}>
														{(provided, snapshot) => (
															<div
																className={`task-step align-middle row ${step.isEditing ? 'editing' : ''}`}
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
																<div className='col-1'>
																	<div className='icon' onClick={() => toggleStepCompleted(step)}>
																		{step.isCompleted === true ? (
																			<Icon name='check-square text-success' />
																		) : (
																			<Icon name='square' stylePrefix='far' />
																		)}
																	</div>
																</div>
																{step.isEditing === true ? (
																	<Fragment>
																		<div className='col-1'>
																			<div className='task-step-bars'>
																				<Icon name='bars' />
																			</div>
																		</div>
																		<div className='col-9'>
																			<InputText
																				name='stepname'
																				label='Name'
																				type='text'
																				placeholder='Name'
																				value={step.name}
																				onChange={(e) => onUpdateStepName(step, e)}
																				isRequired={true}
																				errorMessage='Name Required'
																				onKeyDown={(e) => onFinishEditingStep(step, e)}
																				maxLength={100}
																			/>
																		</div>
																	</Fragment>
																) : (
																	<div className='col-10'>
																		<p className='step-name'>{step.name}</p>
																	</div>
																)}
																<div className='col-1'>
																	<div className='icon' onClick={() => toggleStepEdit(step)}>
																		{!step.baselineTaskStepId ? ( //cannot edit if task step was not passed down from baseline task step
																			step.isEditing === false ? (
																				<Icon name='pencil-alt' />
																			) : (
																				<i className='fa-regular fa-check'></i>
																			)
																		) : null}
																	</div>
																</div>
															</div>
														)}
													</Draggable>
												))}
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
								{task.steps.length < MAX_TASK_STEP_COUNT ? (
									<div className='row'>
										<div className='col-12 remove-mt'>
											<Button type='button' bootstrapClass='link' onClick={onAddStep} text='Add a Step' />
										</div>
									</div>
								) : null}
							</div>
						) : null}

						{isTaskFinancialsActive === true && isMobile === false && (
							<div className='task-detail-section'>
								<div className='title highlighted'>Task Financials</div>
								<div className='row financials-input'>
									<div className='col-6'>
										<InputText
											name='financialsBudget'
											label='Budget'
											type='number'
											placeholder='Budget'
											value={task.financialsBudget ? task.financialsBudget.toString() : ''}
											onChange={onBudgetFieldChange}
											isDisabled={!allowLocationTaskBudgetEntry}
										/>
									</div>
									<div className='col-6'>
										<InputText
											name='financialsActual'
											label='Actual'
											type='number'
											placeholder='Actual'
											value={task.financialsActual ? task.financialsActual.toString() : ''}
											onChange={onBudgetFieldChange}
										/>
									</div>
								</div>
							</div>
						)}

						{isMobile === false && (
							<div className='task-detail-section'>
								<div className='title highlighted'>Task History</div>
								{task.history.length > 0 ? (
									<Fragment>
										<div className='list-group list-group-flush'>
											{task.history.map((h) => (
												<HistoryRow key={h.id} historyItem={h} />
											))}
										</div>
									</Fragment>
								) : (
									<div>
										<Button type='button' bootstrapClass='link showHistory' onClick={onShowHistory} text='Show History' />
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</form>
		</div>
	);
};

LimitedModeTaskDetailDrawerForm.propTypes = {
	// Task
	task: PropTypes.object.isRequired,
	isShowingBasicInfo: PropTypes.bool.isRequired,

	// Form State
	isFormValid: PropTypes.bool.isRequired,
	isCommentsLoading: PropTypes.bool.isRequired,
	isLoadingProjectCoaches: PropTypes.bool.isRequired,

	// User
	currentUserId: PropTypes.number.isRequired,
	customerRole: PropTypes.string.isRequired,

	// State changing behavior
	onTaskChange: PropTypes.func.isRequired,
	onShowHistory: PropTypes.func.isRequired,
	saveCommentClick: PropTypes.func.isRequired,
	onShowMoreComments: PropTypes.func.isRequired,
	onDragEnd: PropTypes.func.isRequired,
	toggleStepCompleted: PropTypes.func.isRequired,
	onUpdateStepName: PropTypes.func.isRequired,
	onAddStep: PropTypes.func.isRequired,
	toggleStepEdit: PropTypes.func.isRequired,
	toggleCertified: PropTypes.func.isRequired,
	toggleEditProjectCoach: PropTypes.func.isRequired,
	onToggleBasicInfo: PropTypes.func.isRequired,
	toggleFeedBackModal: PropTypes.func.isRequired,

	// Drawer behavior
	showContingentDrawer: PropTypes.func.isRequired,
	showPrereqDrawer: PropTypes.func.isRequired,

	// Filters/Features
	allowLocationTaskBudgetEntry: PropTypes.bool.isRequired,
	allowLocationTaskDateChanges: PropTypes.bool.isRequired,
	isProjectWidgetActive: PropTypes.bool.isRequired,
	isTaskFeedbackActive: PropTypes.bool.isRequired,
	isTaskFinancialsActive: PropTypes.bool.isRequired,
	isTaskStepsActive: PropTypes.bool.isRequired,
	projectCoaches: PropTypes.array.isRequired,
	mentionableUsers: PropTypes.array.isRequired,
	responsibleUsers: PropTypes.array.isRequired,
	statuses: PropTypes.array.isRequired,
	taskTags: PropTypes.array.isRequired,

	// File and Links
	isSavingLink: PropTypes.bool.isRequired,
	isSavingFile: PropTypes.bool.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	setTaskLinks: PropTypes.func.isRequired,
	onSaveLink: PropTypes.func.isRequired,
	setSavingLink: PropTypes.func.isRequired,
	uploadFile: PropTypes.func.isRequired,
	setTaskFiles: PropTypes.func.isRequired,
	onSaveFile: PropTypes.func.isRequired,
	setSavingFile: PropTypes.func.isRequired,

	// funky stuff
	isMobile: PropTypes.bool.isRequired,

	locationTaskTranslation: PropTypes.object,
	locationTaskTranslationRating: PropTypes.number,
	onLocationTaskTranslation: PropTypes.func.isRequired,
	onLocationTaskTranslationRating: PropTypes.func.isRequired,
	canTranslate: PropTypes.bool.isRequired,
	translationLabels: PropTypes.object,
	isTranslatingLocationTask: PropTypes.bool.isRequired,
};

export default LimitedModeTaskDetailDrawerForm;